// Client capture orchestration, with injected dependencies so the health-route "no model
// call, no write" invariant is unit-testable with a spy (review B1). The SPA supplies real
// Supabase deps; tests supply fakes.
import { isCloudRoute, type Extraction, type Route } from "./extract";

export interface CaptureDeps {
  newId: () => string;
  insertCapture: (row: {
    id: string;
    family_id: string;
    created_by: string;
    raw_text: string;
    route: "cloud";
    status: "pending";
  }) => Promise<void>;
  extract: (captureId: string) => Promise<Extraction>;
}

export interface StartCaptureInput {
  text: string;
  route: Route;
  memberId: string;
  familyId: string;
}

export type StartCaptureResult =
  | { route: "private_health"; stub: true } // no DB write, no model call
  | { route: "cloud"; captureId: string; extraction: Extraction };

/** Persist-raw-first for the cloud route, then extract. For the health route: write nothing,
 *  call nothing (the boundary is also enforced by the DB CHECK and the Edge Function). */
export async function startCapture(
  input: StartCaptureInput,
  deps: CaptureDeps
): Promise<StartCaptureResult> {
  if (!isCloudRoute(input.route)) {
    return { route: "private_health", stub: true };
  }
  const id = deps.newId();
  await deps.insertCapture({
    id,
    family_id: input.familyId,
    created_by: input.memberId,
    raw_text: input.text,
    route: "cloud",
    status: "pending",
  });
  const extraction = await deps.extract(id);
  return { route: "cloud", captureId: id, extraction };
}

export interface PhotoCaptureDeps {
  newId: () => string;
  uploadPhoto: (path: string) => Promise<void>;
  insertAttachment: (row: { family_id: string; created_by: string; storage_path: string; mime: string }) => Promise<string>;
  insertPhotoCapture: (row: {
    id: string;
    family_id: string;
    created_by: string;
    route: "cloud";
    modality: "photo";
    attachment_id: string;
    status: "pending";
  }) => Promise<void>;
  extract: (captureId: string) => Promise<Extraction>;
}

/** Photo capture. Health route: upload NOTHING, insert NOTHING, call NOTHING (the boundary is
 *  also the DB CHECK forbidding an attachment on a health capture). Cloud route: upload the
 *  image first (raw-first), then attachment + capture, then extract. */
export async function startPhotoCapture(
  input: { route: Route; memberId: string; familyId: string; mime: string },
  deps: PhotoCaptureDeps
): Promise<StartCaptureResult> {
  if (!isCloudRoute(input.route)) {
    return { route: "private_health", stub: true };
  }
  const id = deps.newId();
  const path = `${input.familyId}/${id}`;
  await deps.uploadPhoto(path);
  const attachmentId = await deps.insertAttachment({
    family_id: input.familyId,
    created_by: input.memberId,
    storage_path: path,
    mime: input.mime,
  });
  await deps.insertPhotoCapture({
    id,
    family_id: input.familyId,
    created_by: input.memberId,
    route: "cloud",
    modality: "photo",
    attachment_id: attachmentId,
    status: "pending",
  });
  const extraction = await deps.extract(id);
  return { route: "cloud", captureId: id, extraction };
}

/** REQ-CAPTURE-018 — taking/choosing a photo proceeds STRAIGHT to processing, with no second "Add"
 *  tap dumping the user back to the Add screen. Pure UI decision (so it's testable): a file present →
 *  show a busy state + submit the capture immediately; no file → reset the button to "Add". */
export function onPhotoSelected(
  hasFile: boolean,
  actions: { busy: () => void; reset: () => void; submit: () => void },
): void {
  if (hasFile) {
    actions.busy();
    actions.submit();
  } else {
    actions.reset();
  }
}
